import { TranslateService } from 'Public_old/src/app/Common/Services/translate.service';
import {
	Component,
	Directive,
	ElementRef,
	EnvironmentInjector,
	EventEmitter,
	forwardRef,
	Injector,
	Input,
	inject,
	Optional,
	Output,
	Provider,
	Self,
	ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, UntypedFormControl } from '@angular/forms';
import { GlobalValidator } from '../Classes/globalValidator';

@Component({
	standalone: false,
	selector: 'visiba-select',
	template: /*language=html*/ `
		<div class="wallInput wallStyling inputWrap" [class.witharrow]="showArrow" [class.hasIcon]="icon != null">
			<input
				[tabindex]="tabInd"
				type="text"
				[readonly]="(autocomplete == undefined && !allowInput) || readonly"
				[(ngModel)]="localModel"
				[placeholder]="localPlaceholder"
				(ngModelChange)="search()"
				(click)="toggleOptions()"
				(keyup.esc)="toggleOptions('off')"
				(keyup.enter)="selectOption(targetedOption); prevent($event)"
				(keydown.enter)="prevent($event)"
				(keydown.arrowdown)="arrowMove(1); prevent($event)"
				(keydown.arrowup)="arrowMove(-1); prevent($event)"
			/>
			<div class="svgWrap" *ngIf="localModel != null && !readonly && clearButton" (click)="deselect()">
				<visiba-svg a="X"></visiba-svg>
			</div>
			<div
				class="svgWrap arrow"
				*ngIf="(localModel == null || !clearButton) && showArrow && !readonly"
				[class.open]="showOptions"
				(click)="toggleOptions()"
			>
				<visiba-svg a="ArrowDown"></visiba-svg>
			</div>

			<div #ulWrap class="ulWrap" [hidden]="!showOptions">
				<ul>
					<li
						[class.selected]="selectedOption[value] == opt[value]"
						[class.targeted]="targetedOption[value] == opt[value]"
						(mouseover)="mouseoverOption(opt)"
						*ngFor="let opt of options"
						(click)="selectOption(opt)"
						[innerHtml]="opt.displayLabel"
					></li>
				</ul>
			</div>
			<select *ngIf="showSelect" [(ngModel)]="selectValue" (ngModelChange)="selectOptionFromValue(selectValue)">
				<option *ngFor="let opt of options" [value]="opt[value]">{{ opt[label] }}</option>
			</select>
			<visiba-svg *ngIf="icon != null" class="iconSvg" [a]="icon"></visiba-svg>
			<label [class.value]="localModel != null && localModel != ''" [class.svg]="icon != null">{{ localPlaceholder }}</label>
			<span class="error" [class.value]="localModel != null && localModel != ''">{{ currentError }}</span>
		</div>
	`,
	host: {
		'[class.readonly]': 'readonly',
	},
})
export class VisibaSelectComponent implements ControlValueAccessor {
	@Input() tabInd: number = 0;
	@Input() icon: string = null;
	@Input() placeholder: string = '';
	@Input() label: string = 'label';
	@Input() value: string = 'value';
	@Input() showArrow: boolean = true;
	@Input() autocomplete: string;
	@Input() allowInput: boolean = false;
	@Input() readonly: boolean = false;
	@Input() clearable: boolean = true;
	@Input() errorMessages: { [name: string]: string } | string = 'default';
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	@Input('modelValue') localModel: any = null;
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	@Input('options') coreOptions: Array<any> = [];
	@Input() optionsUrl: string; // /Api/Client/Get?name={{searchStr}}
	@Input() clearButton: boolean = true;
	@Output() onChange = new EventEmitter();

	@ViewChild('ulWrap', { static: false }) ulWrap: ElementRef;

	currentError: string = null;
	showOptions: boolean = false;
	localPlaceholder: string = '';
	selectValue: string = '';
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	options: Array<any> = [];
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	targetedOption: any = {};
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	selectedOption: any = {};
	showSelect: boolean = false;

	constructor(
		private _elementRef: ElementRef,
		@Self() public controlDir: NgControl,
		private translateService: TranslateService,
	) {
		controlDir.valueAccessor = this;
		// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
		let el = _elementRef.nativeElement;
		this.showSelect =
			/(ipad|iphone|ipod|android)/g.test(navigator.userAgent.toLowerCase()) && typeof this.autocomplete === 'undefined';
	}

	ngOnChanges(changed) {
		if (typeof changed.coreOptions !== 'undefined') this.setOptionFromValue();
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	writeValue(val: any) {
		window.setTimeout(() => {
			this.handleErrors();
		});

		if (val === '') val = null;

		let option = this.coreOptions.filter((o) => {
			return o[this.value] == val;
		})[0];

		if (typeof option === 'undefined') {
			this.targetedOption = {};
			this.selectedOption = {};

			this.localModel = null;
		} else {
			this.targetedOption = option;
			this.selectedOption = option;

			this.localModel = option[this.label];
		}
	}

	setOptionFromValue() {
		let option = this.coreOptions.filter((o) => {
			return o[this.value] == this.selectedOption[this.value];
		})[0];

		if (typeof option !== 'undefined') {
			this.targetedOption = option;
			this.selectedOption = option;

			this.localModel = option[this.label];
		}
	}

	//Call to push value
	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	propagateChange = (_: any) => {};

	registerOnChange(fn) {
		this.propagateChange = fn;
	}

	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	registerOnTouch() {}
	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	registerOnTouched() {}

	ngOnInit() {
		this.filterOptions();
		if (this.placeholder !== null && this.placeholder !== '')
			this.localPlaceholder = this.translateService.get(this.placeholder) + (this._elementRef.nativeElement.required ? ' *' : '');
	}

	emitValue(val) {
		this.propagateChange(val);

		this.handleErrors();

		if (typeof this.onChange !== 'undefined') {
			window.setTimeout(() => {
				this.onChange.emit(val);
			}, 10);
		}
	}

	/*** ######## **/
	arrowMove(direction: number) {
		let pos = -1;
		if (this.targetedOption[this.value] !== null)
			pos = this.options.map((o) => o[this.value]).indexOf(this.targetedOption[this.value]);

		let newPos = pos + direction;

		if (newPos >= 0 && newPos < this.options.length) this.targetedOption = this.options[newPos];

		if (this.selectedOption[this.value] === null) this.localModel = this.targetedOption[this.label];

		this.toggleOptions('on');

		let targetElement = this.ulWrap.nativeElement.querySelector('.targeted');
		if (targetElement !== null) {
			this.ulWrap.nativeElement.scrollTop = targetElement.offsetTop - targetElement.clientHeight;
		}
	}

	search() {
		if (this.localModel === '') this.localModel = null;

		this.toggleOptions('on');

		if (this.localModel !== null) {
			let matches = (this.options = this.coreOptions.filter((o) => {
				return o[this.label].toLowerCase().indexOf(this.localModel.toLowerCase()) === 0;
			}));
			if (matches.length !== 0) this.targetedOption = matches[0];
		} else {
			this.targetedOption = {};
		}

		this.filterOptions();
	}

	filterOptions() {
		if (typeof this.coreOptions === 'undefined' || this.coreOptions === null) return;

		if (this.localModel === null || typeof this.autocomplete === 'undefined') this.options = this.coreOptions.slice(0);
		else if (typeof this.autocomplete !== 'undefined')
			this.options = this.coreOptions.filter((o) => {
				return o[this.label].toLowerCase().indexOf(this.localModel.toLowerCase()) === 0;
			});

		this.options = this.options.map((o) => {
			if (this.autocomplete) {
				var regex = new RegExp('(' + this.localModel + ')', 'i');
				o.displayLabel = o[this.label].replace(regex, '<span>$1</span>');
			} else {
				o.displayLabel = o[this.label];
			}
			return o;
		});
	}

	prevent(event: KeyboardEvent) {
		event.stopPropagation();
		event.preventDefault();
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	selectOption(option: any) {
		if (!this.showOptions) {
			this.openList();
			return;
		}

		if (typeof option[this.value] === 'undefined') {
			this.selectOptionFromValue(this.localModel);
			return;
		}

		this.selectedOption = option;
		this.localModel = option[this.label];

		this.toggleOptions('off');
		this.emitValue(option[this.value]);
	}

	selectOptionFromValue(val) {
		if (val === null) return;

		let option = this.coreOptions.filter((o) => {
			return o[this.value].toString().indexOf(val) === 0;
		});

		if (option.length > 0) this.selectOption(option[0]);
	}

	deselect() {
		this.selectedOption = {};
		this.localModel = null;
		this.toggleOptions('off');
		this.emitValue(null);
	}

	// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
	mouseoverOption(option: any) {
		this.targetedOption = option;
	}

	/*** ######## **/
	toggleOptions(status: string = null) {
		if (this.readonly) return;

		this.filterOptions();

		if (status === 'on') this.openList();
		else if (status === 'off') this.closeList();
		else if (this.showOptions) this.closeList();
		else this.openList();

		if (!this.showOptions) {
			this.targetedOption = {};
			if (this.selectedOption[this.value] === null) this.localModel = null;
		}
	}

	handleErrors() {
		this.currentError = GlobalValidator.validateFormControl(
			this.translateService,
			this.controlDir.control as UntypedFormControl,
			this.errorMessages,
		);
	}

	openList(): void {
		if (this.readonly || this.showOptions) return;

		this.showOptions = true;

		window.setTimeout(() => {
			let doc = document.getElementsByTagName('html')[0];
			doc.addEventListener('click', this.listClickCheck, false);
		});

		if (this.selectedOption !== null) {
			this.targetedOption = this.selectedOption;
		}
	}

	listClickCheck = (event) => {
		if (
			this.showOptions &&
			event.target &&
			this._elementRef.nativeElement !== event.target &&
			!this._elementRef.nativeElement.contains(event.target)
		) {
			this.closeList();
		}
	};

	closeList(): void {
		this.showOptions = false;
		let doc = document.getElementsByTagName('html')[0];
		doc.removeEventListener('click', this.listClickCheck, false);
	}
}
